// src/components/QuarterlyReviews.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Avatar,
  alpha,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
  Fade,
  Grow,
  Alert,
  Snackbar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  FileUpload as FileUploadIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Image as ImageIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

const statusColors = {
  pending: { bg: '#fff3e0', text: '#ff9800', label: 'Pending Review' },
  in_progress: { bg: '#e3f2fd', text: '#2196f3', label: 'In Progress' },
  completed: { bg: '#e8f5e9', text: '#4caf50', label: 'Completed' },
  reviewed: { bg: '#f3e5f5', text: '#9c27b0', label: 'Reviewed' },
  approved: { bg: '#e0f7fa', text: '#00bcd4', label: 'Approved' },
  rejected: { bg: '#ffebee', text: '#f44336', label: 'Rejected' },
};

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const years = [2024, 2025, 2026, 2027];

const QuarterlyReviews = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [departments, setDepartments] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [createForm, setCreateForm] = useState({
    phone: '',
    quarter: `Q${Math.floor((new Date().getMonth() + 3) / 3)}`,
    year: new Date().getFullYear(),
  });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    id: null,
    review_status: '',
    comment: '',
    upload_path: '',
  });
  const [uploading, setUploading] = useState(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const getFileDownloadUrl = (filepath) => {
    if (!filepath) return '#';
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    const filename = filepath.split('/').pop();
    return `${baseUrl}/hotel/HR/download_quarterly_review_file.php?path=${encodeURIComponent(filename)}`;
  };

  const getFileIcon = (filepath) => {
    if (!filepath) return <FileUploadIcon />;
    const ext = filepath.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return <PdfIcon />;
      case 'doc':
      case 'docx': return <DocIcon />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return <ImageIcon />;
      default: return <FileUploadIcon />;
    }
  };

  useEffect(() => {
    fetchQuarterlyReviews();
    fetchDepartments();
    fetchAvailableStaff();
  }, [page, rowsPerPage, statusFilter, departmentFilter, searchTerm, quarterFilter, yearFilter]);

  const fetchQuarterlyReviews = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const url = new URL(`${baseUrl}/hotel/HR/quarterly_reviews_api.php`);
      url.searchParams.append('limit', rowsPerPage);
      url.searchParams.append('offset', page * rowsPerPage);
      if (quarterFilter !== 'all') url.searchParams.append('quarter', quarterFilter);
      url.searchParams.append('year', yearFilter);
      if (statusFilter !== 'all') url.searchParams.append('status', statusFilter);
      if (departmentFilter !== 'all') url.searchParams.append('department', departmentFilter);
      if (searchTerm) url.searchParams.append('search', searchTerm);
      
      const response = await fetch(url);
      const result = await response.json();
      
      if (result.success) {
        setReviews(result.data);
        setStats(result.stats);
        setTotalRecords(result.pagination?.total || 0);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching quarterly reviews:', err);
      setError('Failed to load quarterly reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/get_depts_list.php`);
      const result = await response.json();
      if (result.success) {
        setDepartments(result.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

 const fetchAvailableStaff = async () => {
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    // Add for_quarterly_review=true to show all staff regardless of appraisal status
    const url = `${baseUrl}/hotel/HR/get_appraisal_staff.php?min_months=0&include_inactive=false&for_quarterly_review=true`;
    console.log('Fetching staff from:', url);
    
    const response = await fetch(url);
    const result = await response.json();
    
    console.log('Staff API response:', result);
    
    if (result.success) {
      console.log('Staff count:', result.data?.length);
      setAvailableStaff(result.data || []);
    } else {
      console.error('API error:', result.message);
      setAvailableStaff([]);
    }
  } catch (err) {
    console.error('Error fetching staff:', err);
    setAvailableStaff([]);
  }
};
  const handleCreateReview = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/quarterly_reviews_api.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Quarterly review created successfully', severity: 'success' });
        setCreateDialogOpen(false);
        setCreateForm({ phone: '', quarter: `Q${Math.floor((new Date().getMonth() + 3) / 3)}`, year: new Date().getFullYear() });
        fetchQuarterlyReviews();
      } else {
        if (result.already_exists) {
          setSnackbar({ open: true, message: result.message, severity: 'warning' });
        } else {
          setSnackbar({ open: true, message: result.message, severity: 'error' });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to create review', severity: 'error' });
    }
  };

  const handleUpdateStatus = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/quarterly_reviews_api.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Review updated successfully', severity: 'success' });
        setEditDialogOpen(false);
        fetchQuarterlyReviews();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update review', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/quarterly_reviews_api.php?id=${selectedReview?.id}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Review deleted successfully', severity: 'success' });
        setDeleteDialogOpen(false);
        fetchQuarterlyReviews();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete review', severity: 'error' });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('review_id', editForm.id);
    
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${baseUrl}/hotel/HR/upload_quarterly_review_file.php`, {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();
      
      if (result.success) {
        setEditForm(prev => ({ ...prev, upload_path: result.file_path }));
        setSnackbar({ open: true, message: 'File uploaded successfully', severity: 'success' });
        fetchQuarterlyReviews();
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'error' });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar({ open: true, message: 'Failed to upload file', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Full Name', 'Department', 'Phone', 'Email', 'Quarter', 'Year', 'Status', 'Comment', 'Date Created'];
    const csvData = reviews.map(r => [
      r.full_name,
      r.department,
      r.phone,
      r.email,
      r.quarter,
      r.year,
      r.status_display,
      r.comment || '',
      new Date(r.date_created).toLocaleDateString(),
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `quarterly_reviews_${yearFilter}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPieChartData = () => {
    if (!stats) return [];
    return [
      { name: 'Pending', value: stats.pending || 0, color: '#ff9800' },
      { name: 'In Progress', value: stats.in_progress || 0, color: '#2196f3' },
      { name: 'Completed', value: stats.completed || 0, color: '#4caf50' },
      { name: 'Reviewed', value: stats.reviewed || 0, color: '#9c27b0' },
      { name: 'Approved', value: stats.approved || 0, color: '#00bcd4' },
      { name: 'Rejected', value: stats.rejected || 0, color: '#f44336' },
    ].filter(item => item.value > 0);
  };

  const StatCard = ({ title, value, color, icon: Icon, onClick }) => (
    <Grow in timeout={300}>
      <Card 
        sx={{ 
          borderRadius: 3, 
          cursor: onClick ? 'pointer' : 'default',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': onClick ? { transform: 'translateY(-4px)', boxShadow: 6 } : {},
        }}
        onClick={onClick}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="textSecondary">{title}</Typography>
              <Typography variant="h4" fontWeight="bold">{value}</Typography>
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 48, height: 48 }}>
              <Icon />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Grow>
  );

  if (loading && reviews.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading quarterly reviews...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1400px', mx: 'auto', width: '100%', overflow: 'auto', height: '100vh' }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ mt: 2 }}>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">Quarterly Performance Reviews</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>Manage and track staff quarterly performance reviews</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f5f5f5' } }}>
                  Create Review
                </Button>
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={{ bgcolor: 'white', color: theme.palette.primary.main, '&:hover': { bgcolor: '#f5f5f5' } }}>
                  Export Report
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard title="Total" value={stats?.total || 0} color="#1976d2" icon={AssessmentIcon} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard title="Pending" value={stats?.pending || 0} color="#ff9800" icon={PendingIcon} onClick={() => setStatusFilter('pending')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard title="In Progress" value={stats?.in_progress || 0} color="#2196f3" icon={AssessmentIcon} onClick={() => setStatusFilter('in_progress')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard title="Completed" value={stats?.completed || 0} color="#4caf50" icon={CheckCircleIcon} onClick={() => setStatusFilter('completed')} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <StatCard title="Approved" value={stats?.approved || 0} color="#00bcd4" icon={CheckCircleIcon} onClick={() => setStatusFilter('approved')} />
            </Grid>
          </Grid>

          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>Review Status Distribution</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={getPieChartData()} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {getPieChartData().map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>Quick Stats</Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Reviews Created</Typography>
                    <Typography variant="h6" fontWeight="bold">{stats?.total || 0}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={stats?.total ? ((stats?.completed || 0) / stats.total) * 100 : 0} sx={{ height: 8, borderRadius: 4 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Completion Rate</Typography>
                    <Typography variant="h6" fontWeight="bold">{stats?.total ? Math.round(((stats.completed || 0) / stats.total) * 100) : 0}%</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Active Departments</Typography>
                    <Typography variant="body2" fontWeight="bold">{departments.length}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Quarter/Year Filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">Filter by:</Typography>
              <ToggleButtonGroup value={quarterFilter} exclusive onChange={(e, val) => val && setQuarterFilter(val)} size="small">
                <ToggleButton value="all">All Q</ToggleButton>
                <ToggleButton value="Q1">Q1</ToggleButton>
                <ToggleButton value="Q2">Q2</ToggleButton>
                <ToggleButton value="Q3">Q3</ToggleButton>
                <ToggleButton value="Q4">Q4</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                  {years.map(y => (<MenuItem key={y} value={y}>{y}</MenuItem>))}
                </Select>
              </FormControl>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search by name, phone, or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ flex: 2, minWidth: 200 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Status</InputLabel><Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status"><MenuItem value="all">All Status</MenuItem><MenuItem value="pending">Pending</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="reviewed">Reviewed</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="rejected">Rejected</MenuItem></Select></FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}><InputLabel>Department</InputLabel><Select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)} label="Department"><MenuItem value="all">All Departments</MenuItem>{departments.map((dept) => (<MenuItem key={dept.id} value={dept.departments}>{dept.departments}</MenuItem>))}</Select></FormControl>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchQuarterlyReviews}>Refresh</Button>
            </Box>
          </Paper>

          {/* Reviews Table */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell>Staff</TableCell><TableCell>Department</TableCell><TableCell>Quarter/Year</TableCell><TableCell>Status</TableCell><TableCell>Last Updated</TableCell><TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.length === 0 ? (<TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><Typography variant="body1" color="textSecondary">No quarterly review records found</Typography></TableCell></TableRow>) : (
                    reviews.map((review) => (<TableRow key={review.id} hover>
                      <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><Avatar sx={{ bgcolor: statusColors[review.review_status]?.bg || '#e0e0e0', color: statusColors[review.review_status]?.text || '#757575' }}><PersonIcon /></Avatar><Box><Typography variant="body2" fontWeight="medium">{review.full_name}</Typography></Box></Box></TableCell>
                      <TableCell><Chip size="small" icon={<BusinessIcon />} label={review.department || 'N/A'} variant="outlined" /></TableCell>
                      <TableCell><Chip label={`${review.quarter} ${review.year}`} size="small" variant="filled" sx={{ bgcolor: '#1976d2', color: 'white' }} /></TableCell>
                      <TableCell><Chip label={statusColors[review.review_status]?.label || review.status_display} size="small" sx={{ bgcolor: statusColors[review.review_status]?.bg, color: statusColors[review.review_status]?.text, fontWeight: 'medium' }} /></TableCell>
                      <TableCell><Typography variant="caption" color="textSecondary">{review.date_updated ? new Date(review.date_updated).toLocaleDateString() : new Date(review.date_created).toLocaleDateString()}</Typography></TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details"><IconButton size="small" onClick={() => { setSelectedReview(review); setViewDialogOpen(true); }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Edit Status"><IconButton size="small" onClick={() => { setSelectedReview(review); setEditForm({ id: review.id, review_status: review.review_status, comment: review.comment || '', upload_path: review.upload_path || '' }); setEditDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Delete"><IconButton size="small" onClick={() => { setSelectedReview(review); setDeleteDialogOpen(true); }} color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={[5, 10, 25, 50]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, newPage) => setPage(newPage)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
          </Paper>

          <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}><Typography variant="body2" color="textSecondary">Designed by Elegantè IT</Typography></footer>
        </Box>
      </Box>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Quarterly Performance Review</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth><InputLabel>Select Staff</InputLabel><Select value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} label="Select Staff"><MenuItem value="">Select Staff</MenuItem>{availableStaff.map((staff) => (<MenuItem key={staff.id} value={staff.phone}>{staff.full_name} - {staff.department}</MenuItem>))}</Select></FormControl>
          <FormControl fullWidth><InputLabel>Quarter</InputLabel><Select value={createForm.quarter} onChange={(e) => setCreateForm({ ...createForm, quarter: e.target.value })} label="Quarter"><MenuItem value="Q1">Q1 (Jan - Mar)</MenuItem><MenuItem value="Q2">Q2 (Apr - Jun)</MenuItem><MenuItem value="Q3">Q3 (Jul - Sep)</MenuItem><MenuItem value="Q4">Q4 (Oct - Dec)</MenuItem></Select></FormControl>
          <FormControl fullWidth><InputLabel>Year</InputLabel><Select value={createForm.year} onChange={(e) => setCreateForm({ ...createForm, year: e.target.value })} label="Year">{years.map(y => (<MenuItem key={y} value={y}>{y}</MenuItem>))}</Select></FormControl>
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleCreateReview}>Create Review</Button></DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Quarterly Review</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth><InputLabel>Status</InputLabel><Select value={editForm.review_status} onChange={(e) => setEditForm({ ...editForm, review_status: e.target.value })} label="Status"><MenuItem value="pending">Pending Review</MenuItem><MenuItem value="in_progress">In Progress</MenuItem><MenuItem value="completed">Completed</MenuItem><MenuItem value="reviewed">Reviewed</MenuItem><MenuItem value="approved">Approved</MenuItem><MenuItem value="rejected">Rejected</MenuItem></Select></FormControl>
          <TextField label="Comment" multiline rows={4} value={editForm.comment || ''} onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })} fullWidth placeholder="Add your comments about this review..." />
          <Button variant="outlined" component="label" startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />} disabled={uploading}>Upload Performance Document<input type="file" hidden accept=".pdf,.doc,.docx,.jpg,.png" onChange={handleFileUpload} /></Button>
          {editForm.upload_path && (<Alert severity="info" icon={getFileIcon(editForm.upload_path)} action={<Button color="inherit" size="small" href={getFileDownloadUrl(editForm.upload_path)} target="_blank">View</Button>}>File uploaded: {editForm.upload_path.split('/').pop()}</Alert>)}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setEditDialogOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleUpdateStatus} startIcon={<SaveIcon />}>Save Changes</Button></DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedReview && (<><DialogTitle><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><Typography variant="h6">Quarterly Review Details</Typography><Chip label={statusColors[selectedReview.review_status]?.label} sx={{ bgcolor: statusColors[selectedReview.review_status]?.bg, color: statusColors[selectedReview.review_status]?.text }} /></Box></DialogTitle>
        <DialogContent><Grid container spacing={3}>
          <Grid item xs={12} md={6}><Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}><Typography variant="subtitle2" color="textSecondary">Staff Information</Typography><Box sx={{ mt: 2 }}><Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}><Avatar sx={{ width: 56, height: 56, bgcolor: theme.palette.primary.main }}><PersonIcon /></Avatar><Box><Typography variant="h6">{selectedReview.full_name}</Typography><Typography variant="body2" color="textSecondary">{selectedReview.department}</Typography></Box></Box><Divider /><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}><PhoneIcon sx={{ fontSize: 16 }} /><Typography variant="body2">{selectedReview.phone}</Typography></Box><Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}><EmailIcon sx={{ fontSize: 16 }} /><Typography variant="body2">{selectedReview.email}</Typography></Box></Box></Paper></Grid>
          <Grid item xs={12} md={6}><Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}><Typography variant="subtitle2" color="textSecondary">Review Information</Typography><Box sx={{ mt: 2 }}><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}><Typography variant="body2" color="textSecondary">Period:</Typography><Typography variant="body2" fontWeight="bold">{selectedReview.quarter} {selectedReview.year}</Typography></Box><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}><Typography variant="body2" color="textSecondary">Created:</Typography><Typography variant="body2">{new Date(selectedReview.date_created).toLocaleDateString()}</Typography></Box><Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}><Typography variant="body2" color="textSecondary">Last Updated:</Typography><Typography variant="body2">{selectedReview.date_updated ? new Date(selectedReview.date_updated).toLocaleDateString() : 'N/A'}</Typography></Box>{selectedReview.comment && (<><Divider /><Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Comments:</Typography><Typography variant="body2">{selectedReview.comment}</Typography></>)}</Box></Paper></Grid>
          {selectedReview.upload_path && (<Grid item xs={12}><Button startIcon={getFileIcon(selectedReview.upload_path)} variant="outlined" fullWidth href={getFileDownloadUrl(selectedReview.upload_path)} target="_blank">View Uploaded Performance Document</Button></Grid>)}
        </Grid></DialogContent>
        <DialogActions><Button onClick={() => setViewDialogOpen(false)}>Close</Button><Button variant="contained" onClick={() => { setViewDialogOpen(false); setEditForm({ id: selectedReview.id, review_status: selectedReview.review_status, comment: selectedReview.comment || '', upload_path: selectedReview.upload_path || '' }); setEditDialogOpen(true); }}>Update Status</Button></DialogActions></>)}
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}><DialogTitle>Confirm Delete</DialogTitle><DialogContent><Typography>Are you sure you want to delete the quarterly review for <strong>{selectedReview?.full_name}</strong> ({selectedReview?.quarter} {selectedReview?.year})?</Typography><Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>This action cannot be undone.</Typography></DialogContent><DialogActions><Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button><Button onClick={handleDelete} color="error" variant="contained">Delete</Button></DialogActions></Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}><Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert></Snackbar>
    </Box>
  );
};

export default QuarterlyReviews;